import { isDefined } from '@hover/utilities';

import type {
  AiMaterialFragment,
  AiPaintColor,
} from 'src/shared/graphql/generated';
import type { PartialDeep } from 'src/shared/types';

import type {
  CustomizationType,
  PartnerCategoryConfiguration,
} from '../../constants/customizationCategories';
import type { ProductCatalogCustomizeProductsQuery } from '../../graphql/generated';
import { warn } from '../../helpers/logging';
import type {
  AiConfigurationMaterialProduct,
  AiConfigurationPaintProduct,
  ProductCatalogMaterialProduct,
  ProductCatalogPaintProduct,
} from '../../types';

import type {
  ProductCatalogProduct,
  ProductCatalogVariation,
  ProductCatalogVariationFacet,
} from './types';

export const aiPaintColorToDesignerPaintColor = ({
  __typename,
  manufacturer,
  name,
  ...aiPaintColor
}: AiPaintColor): AiConfigurationPaintProduct => ({
  $type: 'color',
  primaryLabel: name,
  secondaryLabel: manufacturer,
  ...aiPaintColor,
});

export const aiMaterialToDesignerMaterial = ({
  __typename,
  hex,
  name,
  ...aiMaterial
}: AiMaterialFragment): AiConfigurationMaterialProduct => ({
  $type: 'material',
  primaryLabel: name,
  ...(hex ? { hex } : {}),
  ...aiMaterial,
});

export type VarationCallback<T> = (
  variation: ProductCatalogVariation,
  product: ProductCatalogProduct,
) => T;

/**
 * Map over all of the variations nested in the list of products returned when
 * querying products from Product Catalog and produce a flat list.
 *
 * @param data Product Catalog products query response
 * @param callback A callback invoked for each variation that receives the
 * variation and the product its nested in to produce a new value
 *
 * @returns a flat list of values produced by the callback
 */
export const mapVariations = <T>(
  data: ProductCatalogCustomizeProductsQuery | undefined,
  callback: VarationCallback<T>,
) => {
  const nodes = data?.productCatalogProducts.nodes;

  if (!nodes) return [];

  return nodes.flatMap(product => {
    if (!product?.variations) return [];

    return product.variations
      .map(variation => callback(variation, product))
      .filter(isDefined);
  }) as Exclude<T, undefined | null>[];
};

interface GetCommonProductFieldsOptions extends PartnerCategoryConfiguration {
  type: CustomizationType;
  spaceType: string;
  region?: string;
}

export type ToDesignerProductOptions = Omit<
  GetCommonProductFieldsOptions,
  'type'
>;

const facetMatching =
  (name: string | undefined, fallback: boolean = false) =>
  (variationFacet: ProductCatalogVariationFacet) => {
    if (!name) return fallback;
    return variationFacet.facetValue?.facet.name === name;
  };

const getInvalidPrefix = (type: CustomizationType) =>
  `Palette: invalid product catalog ${type} variation`;

const getCommonProductFields = (
  {
    type,
    spaceType,
    region,
    paletteFacet,
    primaryLabelFacet,
    secondaryLabelFacet,
  }: GetCommonProductFieldsOptions,
  {
    __typename: _,
    id,
    manufacturerUrl,
    name,
    variationFacets,
  }: ProductCatalogVariation,
) => {
  const palettes =
    variationFacets
      ?.filter(facetMatching(paletteFacet, true))
      .map(f => f.facetValue?.value)
      .filter(isDefined) ?? [];

  const invalidPrefix = getInvalidPrefix(type);

  if (palettes.length === 0) {
    warn(`${invalidPrefix} "${id}" is missing varationFacets (palettes)`);
    return null;
  }

  return {
    id,
    is3dFusion: false,
    manufacturerUrl: manufacturerUrl ?? undefined,
    primaryLabel:
      variationFacets?.find(facetMatching(primaryLabelFacet))?.facetValue
        ?.value ?? name,
    secondaryLabel: variationFacets?.find(facetMatching(secondaryLabelFacet))
      ?.facetValue?.value,
    palettes,
    productCatalogVariationUuid: id,
    regions: region ? [region] : [],
    spaceType,
  };
};

export const productCatalogVariationToDesignerMaterial =
  (options: ToDesignerProductOptions) =>
  ({
    image,
    aiPrompt,
    ...rest
  }: ProductCatalogVariation): ProductCatalogMaterialProduct | null => {
    const type = 'material';
    const common = getCommonProductFields({ ...options, type }, rest);

    if (!common) return null;

    const thumbnail = image?.redirectUrl;
    const invalidPrefix = getInvalidPrefix(type);

    if (!thumbnail) {
      warn(`${invalidPrefix} "${rest.id}" is missing a thumbnail`);
      return null;
    }

    const aiPromptName = aiPrompt?.name;

    if (!aiPromptName) {
      warn(`${invalidPrefix} "${rest.id}" is missing a prompt`);
      return null;
    }

    return {
      ...common,
      $type: type,
      aiPromptName,
      thumbnail,
    };
  };

export const productCatalogVariationToDesignerPaint =
  (options: ToDesignerProductOptions) =>
  (
    { color, ...rest }: ProductCatalogVariation,
    product: PartialDeep<ProductCatalogProduct>,
  ): ProductCatalogPaintProduct | null => {
    const type = 'paint';
    const common = getCommonProductFields({ ...options, type }, rest);

    if (!common) return null;

    const { secondaryLabel } = common;
    const { id } = rest;
    const { region } = options;

    const invalidPrefix = getInvalidPrefix(type);

    const hex = color?.hex;

    if (!hex) {
      warn(`${invalidPrefix} "${id}" is missing a hex color`);
      return null;
    }

    const manufacturer = product?.provider?.name;

    if (!manufacturer) {
      warn(`${invalidPrefix} "${id}" is missing a manufacturer`);
      return null;
    }

    return {
      ...common,
      $type: 'color',
      hex,
      // TODO: it doesn't seem like we use this right now. I think we can
      // probably remove it, unless this won't be handled by the Product Catalog
      // category. Note that in order to handle this via the Product Catalog
      // category, we'll need to update `isCategoryEqual` to treat paint
      // categories as different categories in `components/Customize/menu.ts`.
      paintRegions: region ? [region] : [],
      secondaryLabel: secondaryLabel ?? manufacturer,
    };
  };
